import React from 'react';
import ContextMenu from '../../../ui/ContextMenu';
import CardOverlays from '../../../ui/CardOverlays';
import { MoreVertical, Plus, Minus, Info, RotateCw } from 'lucide-react';

/**
 * Einheitliches ⋮-Menü für Karten in allen DeckEditor-Ansichten — komponiert
 * `ui/ContextMenu` (Radix), keine eigene Menü-Mechanik.
 *
 * Props:
 * - card: Kartenobjekt (geht an onShowDetails/onSwitchPrint)
 * - onShowDetails: „Details" — unabhängig vom Bearbeitungsrecht; onSwitchPrint:
 *   „Print wechseln" — nur mit Recht übergeben
 * - onSetCommander / onNewLabel: Einträge rendern nur bei
 *   vorhandenem Callback (CommanderCardWithMenu übergibt z.B. kein onSetCommander)
 * - extraItems: Array { label, onClick, icon?, danger? } für view-spezifische Einträge
 * - onOpenChange(open): Öffnen/Schließen nach außen (CompactView unterdrückt
 *   darüber ihre Hover-Kartenvorschau)
 * - overlayTrigger: true → Glas-Trigger ui/CardOverlays.Button (Karten-Overlays,
 *   Stacks/Rows/Commander); false → Standard-Trigger des Masters (Tabelle/Kompakt)
 * - deckLabels / cardLabels / onAssignLabel / onRemoveLabel: Label-Sektion;
 *   Label-Einträge schließen das Menü NICHT (keepOpen) — mehrere Labels lassen
 *   sich in einem geöffneten Menü setzen und entfernen
 */
export default function CardContextMenu({
  card,
  onSetCommander,
  onShowDetails,
  onSwitchPrint,
  extraItems = [],
  onOpenChange,
  overlayTrigger = false,
  deckLabels = [],
  cardLabels = [],
  onAssignLabel,
  onRemoveLabel,
  onNewLabel,
}) {
  const assigned = deckLabels.filter((dl) => cardLabels.some((cl) => cl.id === dl.id));
  const unassigned = deckLabels.filter((dl) => !cardLabels.some((cl) => cl.id === dl.id));
  return (
    // stopPropagation am Wrapper: unter dem Trigger liegt die dragbare Karte
    // bzw. Zeile — ohne den Stopp startet ein Pointer-Down auf dem Menü einen Drag.
    <span className="card-context-menu" onPointerDown={(e) => e.stopPropagation()}>
      <ContextMenu
        ariaLabel="Kartenmenü"
        onOpenChange={onOpenChange}
        trigger={overlayTrigger ? <CardOverlays.Button icon={MoreVertical} label="Kartenmenü" /> : undefined}
      >
        {typeof onShowDetails === 'function' && (
          <ContextMenu.Item icon={Info} onSelect={() => onShowDetails(card)}>Details</ContextMenu.Item>
        )}
        {typeof onSwitchPrint === 'function' && (
          <ContextMenu.Item icon={RotateCw} onSelect={() => onSwitchPrint(card)}>Print wechseln</ContextMenu.Item>
        )}
        {typeof onSetCommander === 'function' && (
          <ContextMenu.Item onSelect={onSetCommander}>Als Commander setzen</ContextMenu.Item>
        )}
        {extraItems.map((item, index) => (
          <ContextMenu.Item key={index} icon={item.icon} danger={item.danger} onSelect={item.onClick}>
            {item.label}
          </ContextMenu.Item>
        ))}
        {typeof onNewLabel === 'function' && (
          <>
            <ContextMenu.Separator />
            {assigned.map((label) => (
              <ContextMenu.Item key={`label-assigned-${label.id}`} keepOpen checked onSelect={() => onRemoveLabel(label.id)}>
                <span className="card-label-row card-label-row--assigned">
                  <span className="card-label-row__color" style={{ background: `var(--label-${label.color})` }} />
                  <span className="card-label-row__name">{label.name}</span>
                  <Minus size={13} aria-hidden className="card-label-row__action" />
                </span>
              </ContextMenu.Item>
            ))}
            {unassigned.map((label) => (
              <ContextMenu.Item key={`label-unassigned-${label.id}`} keepOpen checked={false} onSelect={() => onAssignLabel(label)}>
                <span className="card-label-row">
                  <span className="card-label-row__color" style={{ background: `var(--label-${label.color})` }} />
                  <span className="card-label-row__name">{label.name}</span>
                  <Plus size={13} aria-hidden className="card-label-row__action" />
                </span>
              </ContextMenu.Item>
            ))}
            <ContextMenu.Item icon={Plus} onSelect={onNewLabel}>Neues Label…</ContextMenu.Item>
          </>
        )}
      </ContextMenu>
    </span>
  );
}
